import {Survey, SurveyStatus, SurveyType} from 'crqlar-react-common/dist/models/survey/survey.model';
import {SurveyService} from 'crqlar-react-common/dist/services/survey/survey.service';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useQuery} from 'react-query';
import {SingleSelect} from '../../common/newSelect';
import {SURVEY_TYPE_LABEL_KEYS} from '../../common/uiElements/SurveyTypeElement';

const GUEST_SURVEY_TYPES = [SurveyType.PRE_STAY, SurveyType.POST_STAY, SurveyType.PRE_SERVICE, SurveyType.POST_SERVICE];

const isPublished = (survey: Survey) => survey.status === SurveyStatus.PUBLISHED;

interface Props {
    value?: Survey
    onChange: (survey: Survey | undefined) => void
    /** The stay's sector: a survey limited to other sectors does not fit, so it is never preselected. */
    sectorId?: string
    /** Send test: every survey type, drafts selectable, nothing preselected. */
    forTest?: boolean
}

/**
 * The guest surveys to invite to, grouped by type, drafts shown but not selectable. Preselects only when exactly one
 * published survey fits the stay; grouping is presentation and never moves that choice.
 */
export default function SurveyInviteSurveyField({value, onChange, sectorId, forTest = false}: Props) {
    const {t} = useTranslation();
    const surveysQuery = useQuery(
        ['shareableSurveys', forTest],
        () => SurveyService.getAllSurveys({
            type: forTest ? undefined : GUEST_SURVEY_TYPES,
            limit: {offset: 0, limit: 200, sort: [{field: 'name', direction: 'ASC'}]}
        }),
        {refetchOnWindowFocus: false}
    );
    const candidates = (surveysQuery.data?.content ?? []).filter(candidate => !!candidate.id);
    const selectable = (candidate: Survey) => forTest || isPublished(candidate);
    const fits = (candidate: Survey) => isPublished(candidate)
        && (!candidate.sectorIds?.length || (!!sectorId && candidate.sectorIds.includes(sectorId)));
    const fitting = forTest ? [] : candidates.filter(fits);
    const onlyFit = fitting.length === 1 ? fitting[0] : undefined;

    useEffect(() => {
        if (!value && onlyFit) onChange(onlyFit);
    }, [value, onlyFit, onChange]);

    const typeLabel = (type?: SurveyType) => type ? t(SURVEY_TYPE_LABEL_KEYS[type] ?? type) : t('Survey');
    // Grouped by the translated label, so types sharing a word share a group; groups and names A→Z.
    const groups = Array.from(
        candidates.reduce((byLabel, candidate) => {
            const label = typeLabel(candidate.type);
            return byLabel.set(label, [...(byLabel.get(label) ?? []), candidate]);
        }, new Map<string, Survey[]>())
    )
        .map(([label, surveys]) => ({label, surveys: [...surveys].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))}))
        .sort((a, b) => a.label.localeCompare(b.label));

    // Grouped by type; a draft is listed but only selectable in a test, its status as the grey detail.
    const options = groups.flatMap(group => group.surveys.map(candidate => ({
        value: candidate.id!,
        label: candidate.name || candidate.id!,
        group: group.label,
        detail: isPublished(candidate) ? undefined : t('Draft'),
        disabled: !selectable(candidate),
    })));

    return (
        <SingleSelect
            label={t('Survey')}
            placeholder={t('Select survey')}
            value={value?.id ?? ''}
            options={options}
            onChange={id => onChange(candidates.find(candidate => candidate.id === id))}
            loading={surveysQuery.isLoading}
        />
    );
}
